/**
 * `<OnboardRoute>` — the `/onboard` wizard shell. Mounts under `<RequireAuth>`
 * but outside `/w/:slug` (no workspace chrome).
 *
 * Responsibilities:
 *  - Read the surface flag from `GET /api/onboarding/overview`. If
 *    `surface_mode === "full"` the route is inert → redirect to `/`.
 *  - Wizard layout: left group nav + content pane, current step driven by the
 *    `?group=` search param so refresh/deep-link restores position.
 *  - Surface the managed-OAuth landing (`?connector=connected|error`).
 *  - Terminal status mode once `onboarding_state` is `submitted`/`building`
 *    (with an "add more" path back into the wizard).
 *  - "I'm done" submit, nudged once the recommended groups are connected.
 *
 * Per-step copy + group config: `../groups.ts` + `onboarding:` i18n namespace.
 */

import { useAuth } from "@cometchat-ai/auth/client";
import { Button, Skeleton, Spinner, toast } from "@cometchat-ai/ui-dashboard";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useSearchParams } from "react-router";

import { StatusPill } from "../../components/status-pill";
import { useOnboardingOverview, useSubmitOnboarding, type OverviewGroup } from "../api/onboarding";
import { GroupStep } from "../components/GroupStep";
import { StatusMode } from "../components/StatusMode";
import { FIRST_GROUP, ONBOARDING_GROUPS, findGroup } from "../groups";

export function OnboardRoute() {
  const { t } = useTranslation("onboarding");
  const auth = useAuth();
  const slug = auth.workspace?.slug ?? "";

  const overview = useOnboardingOverview(slug);
  const submit = useSubmitOnboarding(slug);

  const [searchParams, setSearchParams] = useSearchParams();
  const groupParam = searchParams.get("group");
  const activeGroup = findGroup(groupParam);

  // Surface the managed-OAuth round-trip result and strip the param.
  useEffect(() => {
    const result = searchParams.get("connector");
    if (!result) return;
    if (result === "connected") toast.success(t("status.connected"));
    else toast.error(t("status.error"));
    void overview.refetch();
    const next = new URLSearchParams(searchParams);
    next.delete("connector");
    setSearchParams(next, { replace: true });
  }, []);

  if (overview.isPending) {
    return <OnboardSkeleton />;
  }

  if (overview.isError) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center gap-4 text-center">
        <p className="text-sm text-muted-foreground">{t("shell.loadError")}</p>
        <Button variant="outline" onClick={() => void overview.refetch()}>
          {t("shell.retry")}
        </Button>
      </div>
    );
  }

  const data = overview.data;

  // Normal workspaces never see the wizard.
  if (data.surfaceMode === "full") {
    return <Navigate to="/" replace />;
  }

  const isTerminal = data.onboardingState === "submitted" || data.onboardingState === "building";
  // `?group` forces the wizard view even after submit (the "add more" path).
  if (isTerminal && !groupParam) {
    return (
      <OnboardShell>
        <StatusMode
          overview={data}
          onAddMore={() => setSearchParams({ group: FIRST_GROUP.key }, { replace: true })}
        />
      </OnboardShell>
    );
  }

  const byKey = new Map<string, OverviewGroup>(data.groups.map((g) => [g.key, g]));
  const requiredGroups = ONBOARDING_GROUPS.filter((g) => g.required);
  const isGroupConnected = (key: string) =>
    (byKey.get(key)?.sources ?? []).some((s) => s.status === "connected");
  const readyToSubmit = requiredGroups.every((g) => isGroupConnected(g.key));

  return (
    <OnboardShell>
      <div className="grid gap-8 lg:grid-cols-[14rem_1fr]">
        {/* Group nav */}
        <nav aria-label={t("shell.nav.label")} className="space-y-1">
          {ONBOARDING_GROUPS.map((g) => {
            const ov = byKey.get(g.key);
            const connectedCount = (ov?.sources ?? []).filter(
              (s) => s.status === "connected" || s.status === "connecting",
            ).length;
            const isActive = g.key === activeGroup.key;
            return (
              <button
                key={g.key}
                type="button"
                onClick={() => setSearchParams({ group: g.key }, { replace: true })}
                aria-current={isActive ? "page" : undefined}
                data-testid={`onboarding-nav-${g.key}`}
                className={[
                  "flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isActive
                    ? "bg-muted font-medium text-foreground"
                    : "text-muted-foreground hover:bg-muted/60",
                ].join(" ")}
              >
                <span className="flex items-center gap-2">
                  {t(`groups.${g.key}.name`)}
                  {g.required ? (
                    <StatusPill tone="info">{t("shell.requiredBadge")}</StatusPill>
                  ) : null}
                </span>
                {connectedCount > 0 ? (
                  <span className="text-xs text-muted-foreground">{connectedCount}</span>
                ) : null}
              </button>
            );
          })}
        </nav>

        {/* Content pane */}
        <div className="space-y-6">
          <div className="space-y-1">
            <h2 className="exp-heading-font text-xl tracking-tight text-foreground">
              {t(`groups.${activeGroup.key}.name`)}
            </h2>
          </div>

          <GroupStep
            slug={slug}
            group={activeGroup}
            overview={byKey.get(activeGroup.key)}
            pendingInvitations={data.pendingInvitations}
          />

          <div className="flex flex-col items-start gap-2 border-t border-border pt-6">
            <p className="text-sm text-muted-foreground">
              {readyToSubmit ? t("shell.submit.ready") : t("shell.submit.hint")}
            </p>
            <Button
              onClick={() =>
                submit.mutate(undefined, {
                  onSuccess: () => toast.success(t("shell.submit.successToast")),
                  onError: () => toast.error(t("shell.submit.errorToast")),
                })
              }
              disabled={submit.isPending}
              data-testid="onboarding-submit"
            >
              {submit.isPending ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" aria-hidden="true" />
                  {t("shell.submit.submitting")}
                </>
              ) : (
                t("shell.submit.cta")
              )}
            </Button>
          </div>
        </div>
      </div>
    </OnboardShell>
  );
}

function OnboardShell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation("onboarding");
  const { signOut } = useAuth();
  return (
    <div className="min-h-dvh bg-background">
      <div className="mx-auto w-full max-w-5xl px-6 py-10">
        <header className="mb-8 flex items-start justify-between gap-4">
          <div className="space-y-2">
            <h1 className="exp-heading-font text-2xl tracking-tight text-foreground">
              {t("shell.title")}
            </h1>
            <p className="max-w-2xl text-sm text-muted-foreground">{t("shell.subtitle")}</p>
          </div>
          {/* Onboarding mode is the user's only surface, so logout must live
              here — they can't reach the dashboard chrome. RequireAuth bounces
              to /login once the session clears. */}
          <Button variant="ghost" size="sm" className="shrink-0" onClick={() => void signOut()}>
            {t("shell.logout")}
          </Button>
        </header>
        {children}
      </div>
    </div>
  );
}

function OnboardSkeleton() {
  return (
    <div className="min-h-dvh bg-background">
      <div className="mx-auto w-full max-w-5xl px-6 py-10">
        <div className="mb-8 space-y-2">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="grid gap-8 lg:grid-cols-[14rem_1fr]">
          <div className="space-y-2">
            {Array.from({ length: 7 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
          <div className="space-y-3">
            <Skeleton className="h-4 w-2/3" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
